import { storableError } from '../../util/errors';
import { setCurrentUser } from '../../ducks/user.duck';
import { logout } from '../../ducks/auth.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { deleteUserAccount } from '../../util/api';

// ================ Action types ================ //

export const DELETE_ACCOUNT_REQUEST = 'app/ManageAccountPage/DELETE_ACCOUNT_REQUEST';
export const DELETE_ACCOUNT_SUCCESS = 'app/ManageAccountPage/DELETE_ACCOUNT_SUCCESS';
export const DELETE_ACCOUNT_ERROR = 'app/ManageAccountPage/DELETE_ACCOUNT_ERROR';

export const RESET_PASSWORD_REQUEST = 'app/ManageAccountPage/RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'app/ManageAccountPage/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'app/ManageAccountPage/RESET_PASSWORD_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ManageAccountPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ManageAccountPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ManageAccountPage/UPDATE_PROFILE_ERROR';

export const MARK_ACCOUNT_DELETED = 'app/ManageAccountPage/MARK_ACCOUNT_DELETED';

// ================ Reducer ================ //

const initialState = {
  deleteAccountError: null,
  deleteAccountInProgress: false,
  accountDeletionConfirmed: false,
  accountMarkedDeleted: false,
  resetPasswordInProgress: false,
  resetPasswordError: null,
  updateProfileInProgress: false,
  updateProfileError: null,
};

export default function manageAccountPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case DELETE_ACCOUNT_REQUEST:
      return {
        ...state,
        deleteAccountInProgress: true,
        deleteAccountError: null,
        accountDeletionConfirmed: false,
      };
    case DELETE_ACCOUNT_SUCCESS:
      return { ...state, deleteAccountInProgress: false, accountDeletionConfirmed: true };
    case DELETE_ACCOUNT_ERROR:
      return { ...state, deleteAccountInProgress: false, deleteAccountError: payload };

    case RESET_PASSWORD_REQUEST:
      return { ...state, resetPasswordInProgress: true, resetPasswordError: null };
    case RESET_PASSWORD_SUCCESS:
      return { ...state, resetPasswordInProgress: false };
    case RESET_PASSWORD_ERROR:
      return { ...state, resetPasswordInProgress: false, resetPasswordError: payload };

    case UPDATE_PROFILE_REQUEST:
      return { ...state, updateProfileInProgress: true, updateProfileError: null };
    case UPDATE_PROFILE_SUCCESS:
      return { ...state, updateProfileInProgress: false };
    case UPDATE_PROFILE_ERROR:
      return { ...state, updateProfileInProgress: false, updateProfileError: payload };

    case MARK_ACCOUNT_DELETED:
      return { ...state, accountMarkedDeleted: true };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const deleteAccountRequest = () => ({ type: DELETE_ACCOUNT_REQUEST });
export const deleteAccountSuccess = () => ({ type: DELETE_ACCOUNT_SUCCESS });
export const deleteAccountError = e => ({ type: DELETE_ACCOUNT_ERROR, payload: e, error: true });

export const resetPasswordRequest = () => ({ type: RESET_PASSWORD_REQUEST });
export const resetPasswordSuccess = () => ({ type: RESET_PASSWORD_SUCCESS });
export const resetPasswordError = e => ({ type: RESET_PASSWORD_ERROR, payload: e, error: true });

export const updateProfileRequest = () => ({ type: UPDATE_PROFILE_REQUEST });
export const updateProfileSuccess = () => ({ type: UPDATE_PROFILE_SUCCESS });
export const updateProfileError = e => ({ type: UPDATE_PROFILE_ERROR, payload: e, error: true });

export const markAccountDeleted = () => ({ type: MARK_ACCOUNT_DELETED });

// ================ Thunks ================ //

export const deleteAccount = params => (dispatch, getState, sdk) => {
  dispatch(deleteAccountRequest());

  return deleteUserAccount(params)
    .then(() => {
      dispatch(deleteAccountSuccess());
      dispatch(markAccountDeleted());
      return dispatch(logout());
    })
    .catch(e => {
      dispatch(deleteAccountError(storableError(e)));
      // Note: this throws so the caller can catch the error and show it in the UI
      throw e;
    });
};

export const resetPassword = email => (dispatch, getState, sdk) => {
  dispatch(resetPasswordRequest());

  return sdk.passwordReset
    .request({ email })
    .then(() => dispatch(resetPasswordSuccess()))
    .catch(e => {
      dispatch(resetPasswordError(storableError(e)));
      throw e;
    });
};

export const updateProfile = actionPayload => (dispatch, getState, sdk) => {
  dispatch(updateProfileRequest());

  const queryParams = {
    expand: true,
  };

  return sdk.currentUser
    .updateProfile(actionPayload, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const currentUser = entities[0];

      // Update current user in state.user.currentUser through user.duck.js
      dispatch(setCurrentUser(currentUser));
      dispatch(updateProfileSuccess());
      return response;
    })
    .catch(e => {
      dispatch(updateProfileError(storableError(e)));
    });
};
